import React, { useState } from 'react';
import {
    Box, Card, CardContent, Tab, Tabs, TextField,
    Button, Typography, Alert, CircularProgress
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../contexts/AuthContext';
import './Login.css';

function Login() {
    const [tab, setTab] = useState(0); // 0 = sign in, 1 = create account
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const [resendingVerification, setResendingVerification] = useState(false);
    const [needsVerification, setNeedsVerification] = useState(false);

    const { login, register, resendVerificationEmail, requestPasswordReset } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const redirectTo = location.state?.from || '/';

    const clearMessages = () => {
        setError('');
        setSuccess('');
        setNeedsVerification(false);
    };

    const handleTabChange = (_, newValue) => {
        setTab(newValue);
        setShowForgotPassword(false);
        clearMessages();
    };

    const handleShowForgotPassword = () => {
        setShowForgotPassword(true);
        clearMessages();
    };

    const handleBackToSignIn = () => {
        setShowForgotPassword(false);
        clearMessages();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        clearMessages();
        setLoading(true);
        try {
            if (showForgotPassword) {
                const data = await requestPasswordReset(email);
                setSuccess(data.message || 'If an account exists for that email, a password reset link has been sent.');
                return;
            }

            if (tab === 0) {
                await login(email, password);
                navigate(redirectTo, { replace: true });
            } else {
                if (password.length < 8) {
                    setError('Password must be at least 8 characters.');
                    setLoading(false);
                    return;
                }
                if (!firstName.trim() || !lastName.trim()) {
                    setError('First name and last name are required.');
                    setLoading(false);
                    return;
                }
                const data = await register(email, password, firstName.trim(), lastName.trim());
                if (data.email_verification_sent) {
                    setSuccess(`Account created. We sent a verification email to ${email}. Verify your email before signing in.`);
                } else {
                    setSuccess('Account created, but we could not send the verification email. Please contact support before signing in.');
                }
                setPassword('');
            }
        } catch (err) {
            const detail = err.response?.data?.detail;
            if (err.response?.status === 403 && detail === 'Please verify your email before signing in.') {
                setNeedsVerification(true);
            }
            setError(detail || 'Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleResendVerification = async () => {
        clearMessages();
        setResendingVerification(true);
        try {
            await resendVerificationEmail(email, password);
            setSuccess(`Verification email resent to ${email}.`);
            setNeedsVerification(false);
        } catch (err) {
            const detail = err.response?.data?.detail;
            setError(detail || 'Could not resend the verification email. Please try again.');
        } finally {
            setResendingVerification(false);
        }
    };

    const pageTitle = showForgotPassword
        ? 'Reset Password'
        : tab === 0
            ? 'Sign In'
            : 'Create Account';

    return (
        <Box className="login-page">
            <Helmet>
                <title>{pageTitle} | Hockey Pipeline</title>
            </Helmet>

            <Card className="login-card" elevation={0}>
                <CardContent sx={{ p: 3 }}>
                    <div className="login-logo">
                        <img src="/logo@4x.png" alt="Hockey Pipeline" />
                    </div>

                    {showForgotPassword ? (
                        <>
                            <Typography variant="h5" component="h1" align="center" sx={{ fontWeight: 700, mb: 1 }}>
                                Reset your password
                            </Typography>
                            <Typography variant="body2" align="center" sx={{ color: 'var(--text-muted)', mb: 2 }}>
                                Enter your email and we&apos;ll send you a link to choose a new password.
                            </Typography>
                        </>
                    ) : (
                        <Tabs
                            value={tab}
                            onChange={handleTabChange}
                            variant="fullWidth"
                            className="login-tabs"
                            sx={{ mb: 3 }}
                        >
                            <Tab label="Sign In" />
                            <Tab label="Create Account" />
                        </Tabs>
                    )}

                    <Box component="form" onSubmit={handleSubmit} noValidate>
                        {!showForgotPassword && tab === 1 && (
                            <>
                                <TextField
                                    fullWidth
                                    required
                                    label="First name"
                                    value={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                    margin="normal"
                                    autoComplete="given-name"
                                    sx={fieldSx}
                                />
                                <TextField
                                    fullWidth
                                    required
                                    label="Last name"
                                    value={lastName}
                                    onChange={(e) => setLastName(e.target.value)}
                                    margin="normal"
                                    autoComplete="family-name"
                                    sx={fieldSx}
                                />
                            </>
                        )}
                        <TextField
                            fullWidth
                            required
                            label="Email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            margin="normal"
                            autoComplete="email"
                            sx={fieldSx}
                        />
                        {!showForgotPassword && (
                            <TextField
                                fullWidth
                                required
                                label="Password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                margin="normal"
                                autoComplete={tab === 0 ? 'current-password' : 'new-password'}
                                helperText={tab === 1 ? 'Minimum 8 characters' : ''}
                                sx={fieldSx}
                            />
                        )}

                        {error && (
                            <Alert severity="error" sx={{ mt: 2, borderRadius: 2, backgroundColor: '#2d1515', color: '#fca5a5', '& .MuiAlert-icon': { color: '#f87171' } }}>
                                {error}
                                {needsVerification && (
                                    <Button
                                        type="button"
                                        variant="text"
                                        size="small"
                                        disabled={resendingVerification}
                                        onClick={handleResendVerification}
                                        sx={{ ml: 1, color: '#fca5a5', textDecoration: 'underline', fontWeight: 700 }}
                                    >
                                        {resendingVerification ? 'Sending...' : 'Resend verification email'}
                                    </Button>
                                )}
                            </Alert>
                        )}

                        {success && (
                            <Alert severity="success" sx={{ mt: 2, borderRadius: 2, backgroundColor: '#102417', color: '#bbf7d0', '& .MuiAlert-icon': { color: '#86efac' } }}>
                                {success}
                            </Alert>
                        )}

                        {tab === 0 && !showForgotPassword && (
                            <Typography
                                variant="body2"
                                align="right"
                                sx={{ mt: 1 }}
                            >
                                <Button
                                    type="button"
                                    variant="text"
                                    size="small"
                                    onClick={handleShowForgotPassword}
                                    sx={{
                                        p: 0,
                                        minWidth: 0,
                                        textTransform: 'none',
                                        color: 'var(--brand-strong)',
                                        fontWeight: 600,
                                        '&:hover': { color: '#b8de8f', backgroundColor: 'transparent' },
                                    }}
                                >
                                    Forgot password?
                                </Button>
                            </Typography>
                        )}

                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            disabled={loading}
                            className="login-submit-btn"
                            disableElevation
                        >
                            {loading
                                ? <CircularProgress size={22} sx={{ color: '#fff' }} />
                                : showForgotPassword
                                    ? 'Send Reset Link'
                                    : tab === 0
                                        ? 'Sign In'
                                        : 'Create Account'
                            }
                        </Button>
                    </Box>

                    {showForgotPassword ? (
                        <Typography
                            variant="body2"
                            align="center"
                            sx={{ mt: 2, color: '#6b7280', cursor: 'pointer', '&:hover': { color: '#e2e8f0' } }}
                            onClick={handleBackToSignIn}
                        >
                            <span className="login-link-accent">Back to Sign In</span>
                        </Typography>
                    ) : tab === 0 ? (
                        <Typography
                            variant="body2"
                            align="center"
                            sx={{ mt: 2, color: '#6b7280', cursor: 'pointer', '&:hover': { color: '#e2e8f0' } }}
                            onClick={() => setTab(1)}
                        >
                            Don&apos;t have an account? <span className="login-link-accent">Sign up</span>
                        </Typography>
                    ) : (
                        <Typography
                            variant="body2"
                            align="center"
                            sx={{ mt: 2, color: '#6b7280', cursor: 'pointer', '&:hover': { color: '#e2e8f0' } }}
                            onClick={() => setTab(0)}
                        >
                            Already have an account? <span className="login-link-accent">Sign in</span>
                        </Typography>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
}

const fieldSx = {
    '& .MuiOutlinedInput-root': {
        color: 'var(--text-primary)',
        borderRadius: 2,
        '& fieldset': { borderColor: '#2d3344' },
        '&:hover fieldset': { borderColor: '#4a5568' },
        '&.Mui-focused fieldset': { borderColor: 'var(--brand-strong)' },
    },
    '& .MuiInputLabel-root': { color: 'var(--text-muted)' },
    '& .MuiInputLabel-root.Mui-focused': { color: 'var(--brand-strong)' },
    '& .MuiFormHelperText-root': { color: 'var(--text-muted)' },
};

export default Login;
